import {keepPreviousData, useQuery} from '@tanstack/react-query';
import {getWebhookDeliveries, getWebhookDelivery, getWebhookDeliveryStats, resendWebhookDelivery} from '@verdocs/js-sdk';
import {IListWebhookDeliveriesParams, IWebhookDeliveriesResponse, IWebhookDeliveryDetail, VerdocsEndpoint} from '@verdocs/js-sdk';
import {queryClient} from './queryClient';
import {invalidateQuery} from './utils';

export const useWebhookDeliveries = (organizationId: string | undefined, params: IListWebhookDeliveriesParams) =>
  useQuery({
    enabled: !!organizationId,
    queryKey: ['webhook_deliveries', organizationId, params],
    queryFn: () => getWebhookDeliveries(VerdocsEndpoint.getDefault(), params),
    staleTime: 0,
    placeholderData: keepPreviousData,
  });

export const useWebhookDeliveryStats = (organizationId: string | undefined, days: number) =>
  useQuery({
    enabled: !!organizationId,
    queryKey: ['webhook_delivery_stats', organizationId, days],
    queryFn: () => getWebhookDeliveryStats(VerdocsEndpoint.getDefault(), days),
    staleTime: 0,
  });

export const useWebhookDelivery = (organizationId: string | undefined, deliveryId: string) =>
  useQuery({
    enabled: !!organizationId,
    queryKey: ['webhook_delivery', organizationId, deliveryId],
    queryFn: () => getWebhookDelivery(VerdocsEndpoint.getDefault(), deliveryId),
    staleTime: 0,
    // Payloads include recipient data, so keep them out of the persisted cache
    gcTime: 0,
  });

export const resendDelivery = async (organizationId: string | undefined, deliveryId: string) => {
  const delivery = await resendWebhookDelivery(VerdocsEndpoint.getDefault(), deliveryId);

  // Patch in place so resent rows don't vanish from filtered pages
  queryClient.setQueriesData<IWebhookDeliveriesResponse>({queryKey: ['webhook_deliveries', organizationId]}, (old) =>
    old ? {...old, deliveries: old.deliveries.map((row) => (row.id === deliveryId ? {...row, ...delivery} : row))} : old,
  );
  queryClient.setQueryData<IWebhookDeliveryDetail>(['webhook_delivery', organizationId, deliveryId], (old) =>
    old ? {...old, ...delivery} : old,
  );
  invalidateQuery(['webhook_delivery', organizationId, deliveryId]);
  invalidateQuery(['webhook_delivery_stats', organizationId]);
  invalidateQuery(['organization_webhook']);

  return delivery;
};
